/**
 * The "you are framed" cue.
 *
 * Two short notes, played the moment the guide oval turns green on either camera step.
 * People hold the phone at arm's length and point it at their own face, so the one thing
 * they cannot reliably see is the small chip that says they can press the button now — a
 * sound carries that, and needs no language support from the device.
 *
 * Synthesised rather than shipped as an audio file, for the same reason as the success
 * chime: two oscillators against a request and an asset.
 *
 * Never played while the video step is recording — the microphone is live, and the phone's
 * own noises would land in the clip the backend has to verify.
 */

let audioContext = null;

function context() {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!audioContext) audioContext = new Ctor();
  return audioContext;
}

/**
 * Wake the audio hardware from a user gesture.
 *
 * An AudioContext created outside a tap starts suspended, and on iOS stays that way — so
 * the first cue would be silent. Called from every tap that leads into a camera step: the
 * welcome screen's button, and the resume dialog's, which skips the welcome screen
 * entirely.
 */
export function primeCue() {
  try {
    const audio = context();
    if (audio && audio.state === "suspended") audio.resume();
  } catch (e) {
    // Audio is a courtesy. A device that refuses it is not a broken flow.
  }
}

/** A rising two-note figure: short, quiet, and clearly an answer rather than an alarm. */
export function playReadyCue() {
  try {
    const audio = context();
    if (!audio) return;
    if (audio.state === "suspended") audio.resume();

    const now = audio.currentTime;
    [
      [880, 0],
      [1174.66, 0.12],
    ].forEach(([frequency, offset]) => {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = "sine";
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.16, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.18);
      osc.connect(gain).connect(audio.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.2);
    });
  } catch (e) {
    // As above: silence is an acceptable outcome, an exception is not.
  }
}
